import type { SawRecentBundle } from "../repositories/sawWriteRepository";

export const POSITIONS = [
	{ key: "TL", label: "Top Left" },
	{ key: "TR", label: "Top Right" },
	{ key: "MC", label: "Middle Center" },
	{ key: "BL", label: "Bottom Left" },
	{ key: "BR", label: "Bottom Right" },
] as const;

export type Position = (typeof POSITIONS)[number]["key"];
export type MeasurementValues = Record<Position, string>;

export const EMPTY_VALUES: MeasurementValues = {
	TL: "",
	TR: "",
	MC: "",
	BL: "",
	BR: "",
};

// 101-9/16 · 101 9/16 · 9/16
const FRACTION = /^(?:(\d+)[\s-]+)?(\d+)\/(\d+)$/;

// Rounded to the DB's DECIMAL(10,2): OK/Out shown = what gets saved.
export const toNumber = (raw: string): number | null => {
	const text = raw.trim();
	if (text === "") return null;
	const fraction = FRACTION.exec(text);
	const value = fraction
		? Number(fraction[1] ?? 0) + Number(fraction[2]) / Number(fraction[3])
		: Number(text);
	return Number.isFinite(value) ? Math.round(value * 100) / 100 : null;
};

type MeasurementBounds = Pick<
	SawRecentBundle,
	"MinLength" | "MaxLength" | "AvgLength"
>;

export const isOutOfSpec = (
	bundle: MeasurementBounds | null,
	raw: string,
): boolean => {
	const value = toNumber(raw);
	if (value == null || bundle?.MinLength == null || bundle.MaxLength == null)
		return false;
	return value < bundle.MinLength || value > bundle.MaxLength;
};

// Pieces can't measure more than their cut length (+1" tape) nor way shorter;
// likely a typo. Anchor = tracked cut length, else spec max. Warn, never block.
const plausibleAnchor = (bundle: MeasurementBounds | null): number | null =>
	bundle?.AvgLength ?? bundle?.MaxLength ?? null;

const PLAUSIBLE_OVER_IN = 1;

export const isImplausible = (
	bundle: MeasurementBounds | null,
	raw: string,
): boolean => {
	const value = toNumber(raw);
	if (value == null) return false;
	if (value <= 0) return true;
	const anchor = plausibleAnchor(bundle);
	return (
		anchor != null &&
		(value > anchor + PLAUSIBLE_OVER_IN || value < anchor * 0.8)
	);
};

// Editing starts from the saved values: a partial re-entry must not null the
// untouched positions.
export const valuesFor = (bundle: SawRecentBundle | null): MeasurementValues =>
	bundle?.HasCheck
		? {
				TL: bundle.CheckTL == null ? "" : String(bundle.CheckTL),
				TR: bundle.CheckTR == null ? "" : String(bundle.CheckTR),
				MC: bundle.CheckMC == null ? "" : String(bundle.CheckMC),
				BL: bundle.CheckBL == null ? "" : String(bundle.CheckBL),
				BR: bundle.CheckBR == null ? "" : String(bundle.CheckBR),
			}
		: { ...EMPTY_VALUES };

// null = all plausible
export const unusualMessage = (
	bundle: MeasurementBounds | null,
	values: MeasurementValues,
): string | null => {
	const suspects = POSITIONS.filter((p) =>
		isImplausible(bundle, values[p.key]),
	);
	if (suspects.length === 0) return null;
	const anchor = plausibleAnchor(bundle);
	return `Some measurements look unlikely for this bundle${
		anchor != null ? ` (cut at ~${anchor}")` : ""
	}: ${suspects
		.map((p) => `${p.label} ${toNumber(values[p.key])}"`)
		.join(", ")}. Save anyway?`;
};

export const toCheckFields = (values: MeasurementValues) => ({
	checkTL: toNumber(values.TL),
	checkTR: toNumber(values.TR),
	checkMC: toNumber(values.MC),
	checkBL: toNumber(values.BL),
	checkBR: toNumber(values.BR),
});

export interface QualityStatus {
	label: string;
	color: string;
	pending: boolean;
	icon?: "warning" | "check" | "info";
}

export const NO_CHECK = "No check";

export const qualityStatus = (bundle: SawRecentBundle): QualityStatus => {
	if (bundle.IsCutback)
		return { label: "Cutback", color: "warning.main", pending: false };
	if (bundle.IsReclassified || bundle.ReclassifiedToProductId != null)
		return { label: "Reclassified", color: "#7e57c2", pending: false };
	const values = valuesFor(bundle);
	const count = POSITIONS.filter((p) => toNumber(values[p.key]) != null).length;
	if (count === 0) return { label: NO_CHECK, color: "#616161", pending: true };
	if (POSITIONS.some((p) => isOutOfSpec(bundle, values[p.key])))
		return {
			label: "Needs decision",
			color: "#c62828",
			pending: true,
			icon: "warning",
		};
	if (count < 5)
		return {
			label: `Partial ${count}/5`,
			color: "warning.dark",
			pending: true,
			icon: "warning",
		};
	return bundle.MinLength != null && bundle.MaxLength != null
		? { label: "Checked", color: "#2e7d32", pending: false, icon: "check" }
		: // measured, but nothing to compare against
			{ label: "Checked", color: "#3d5e9e", pending: false, icon: "info" };
};
